import { StoredObject } from "@/features/types";
import AppButton from "@/features/line-sheet-sets/app-button";
import SVG from "react-inlinesvg";
import { Box, Flex, Image, Spinner, Text } from "@chakra-ui/react";
import React, { DragEventHandler, useCallback, useEffect, useRef, useState } from "react";
import useDownloadUnoFileInfinite from "@/hooks/use-download-uno-file-infinite";
import { postProcessYWRInfiniteData } from "@/features/ui/helpers/ywr-helpers";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import { PRODUCT_IMAGE_SIZE } from "@/features/order-sheet-sets/product-image-cell";
import { toTitle } from "@/utils/case";
import { useTranslation } from "react-i18next";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";

interface AppImageFileControllerProps {
  buttonText?: string;
  value: File | Pick<StoredObject, "id" | "name"> | undefined;
  onChange: (value: File | null) => void;
  isLoading?: boolean;
}
export default function AppImageFileController({
  buttonText = "파일 업로드",
  value,
  onChange,
  isLoading = false,
}: AppImageFileControllerProps) {
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const { error: showError } = useAppToasts();
  const [uploadedFile, setUploadedFile] = React.useState<File | null>(null);
  const { warning: showWarning } = useAppToasts();
  const { t } = useTranslation();
  const { tSentence } = useI18nHelper();

  const {
    data: downloadUnoFileData,
    fire: fireDownloadUnoFile,
    clear: clearDownloadUnoFile,
  } = useDownloadUnoFileInfinite();

  useEffect(() => {
    postProcessYWRInfiniteData(
      downloadUnoFileData,
      clearDownloadUnoFile,
      showError,
      () => {}
    );
  }, [downloadUnoFileData, clearDownloadUnoFile, showError]);


  const handleOnDrop: DragEventHandler<HTMLDivElement> = (event) => {
    event.stopPropagation();
    event.preventDefault();
    setIsDragging(false);
    handleBinariesAdd(event.dataTransfer.files);
  };

  const handleOnDragEnter: DragEventHandler<HTMLDivElement> = (event) => {
    setIsDragging(true);
  };

  const handleOnDragLeave: DragEventHandler<HTMLDivElement> = () => {
    setIsDragging(false);
  };

  const handleDragOver: DragEventHandler<HTMLDivElement> = (event) => {
    event.stopPropagation();
    event.preventDefault();
  };

  const handleBinariesAdd = useCallback(
    (files: FileList | null) => {
      if (files) {
        if (files.length > 1) {
          showWarning("Only one file is allowed.");
          return;
        }
        const file = files[0];
        if (file) {
          setUploadedFile(file);
          onChange(file);
        }
      }
    },
    [showWarning, onChange]
  );

  const fileInputRef = useRef<HTMLInputElement>(null);

  if (isLoading) {
    return <Flex
      flexDirection={"row"}
      height={"40px"}
      width={"280px"}
      alignItems={"center"}
      justifyContent={"center"}
    >
      <Spinner size={"sm"} />
    </Flex>
  }

  if (!value) {
    return (
      <Box>
        <Flex
          border={"0.5px dashed #8F8F8C"}
          borderColor={isDragging ? "#1272EF" : "#8F8F8C"}
          borderRadius={"4px"}
          padding={"12px 20px"}
          flexDirection={"column"}
          alignItems={"center"}
          justifyContent={"space-between"}
          height={"96px"}
          onDrop={handleOnDrop}
          onDragEnter={handleOnDragEnter}
          onDragLeave={handleOnDragLeave}
          onDragOver={handleDragOver}
        >
          <Text fontSize={"12px"} textAlign={"center"}>
            {tSentence("drag_and_drop_file_here")}
            <br />
            {toTitle(t("or"))}
          </Text>
          <AppButton
            onClick={() => {
              if (fileInputRef.current) {
                fileInputRef.current.click();
              }
            }}
          >
            {toTitle(t("choose_file"))}
          </AppButton>

          <input
            type={"file"}
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                onChange(file);
                setUploadedFile(file);
              }
              e.target.value = "";
            }}
          />
        </Flex>
      </Box>

    );
  }

  const fileExtension = value ? value.name.split('.').pop() : null;
  let fileIconSrc = "/icons-v1_1/file.svg";
  if (fileExtension) {
    switch (fileExtension.toLowerCase()) {
      case "pdf":
        fileIconSrc = "/icons-v1_1/file-pdf.svg";
        break;
      case "xls":
      case "xlsx":
        fileIconSrc = "/icons-v1_1/file-xls.svg";
        break;
      default:
        fileIconSrc = "/icons-v1_1/file.svg";
    }
  }

  if (!uploadedFile) {
    return null
  }

  return (
    <Flex
      flexDirection={"row"}
      // gap={"12px"}
      // height={"80px"}
      // width={"280px"}
      backgroundColor={"white"}
      alignItems={"center"}
      justifyContent={"space-between"}
    >
      <Flex width={"80px"}>
        <Image
          borderRadius={"8px"}
          border={"0.5px solid var(--Pathrade-Gray-Groomy, #8F8F8C)"}
          background={"#fff"}
          flexGrow={"1"}
          alt={"alt"}
          src={
            URL.createObjectURL(uploadedFile)
          }
          boxSize={`${PRODUCT_IMAGE_SIZE}px`}
          objectFit={"contain"}
          zIndex={"10"}
        />
      </Flex>

      <Flex>
        <Flex
          onClick={() => {
            if (fileInputRef.current) {
              fileInputRef.current.click();
            }
          }}
          marginRight={"16px"}
          alignItems={"center"}
          gap={"4px"}
          cursor={"pointer"}
        >
          <SVG
            width={"16"}
            height={"16"}
            src={"/icons-v1_1/edit.svg"}
            style={{ cursor: "pointer" }}
          />
          <Text style={{
            fontSize: "12px",
            fontWeight: "700",
          }}>
            수정
          </Text>
        </Flex>
        <Flex
          onClick={() => {
            onChange(null);
          }}
          marginRight={"16px"}
          alignItems={"center"}
          gap={"4px"}
          cursor={"pointer"}
        >
          <SVG
            width={"16"}
            height={"16"}
            src={"/icons-v1_1/trash.svg"}
            style={{ cursor: "pointer" }}
          />
          <Text style={{
            fontSize: "12px",
            fontWeight: "700",
          }}>
            삭제
          </Text>
        </Flex>
        <input
          type={"file"}
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              setUploadedFile(file);
              onChange(file);
            }
            e.target.value = "";
          }}
        />
      </Flex>
    </Flex>
  );
}
